import { getBackendUrl } from '../utils/utils';
import { createLogger } from '../utils/logger';

const logger = createLogger('assignmentService');

// --- Shared types ---

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface ListParams {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  search?: string;
  assignment_status?: 'draft' | 'published' | 'expired' | 'archived' | 'all';
  bundle_status?: 'draft' | 'published' | 'archived' | 'all';
  // Super-admin-only: scope listBundles to one org (+ platform-wide). Ignored for
  // non-super-admins, who are always scoped to their own org server-side.
  organization_id?: string;
}

// --- Bundle types ---

export type BundleStatus = 'draft' | 'published' | 'archived';
export type BundleItemType = 'scenario' | 'prompt';
// Adaptive Difficulty a bundle author can pin to a prompt item. Mirrors the
// backend DifficultyMode; null/undefined means "no forced difficulty" (the
// trainee picks their own). Only meaningful for 'prompt' items.
export type BundleItemDifficulty = 'easy' | 'medium' | 'hard' | 'random';

export interface BundleItem {
  id: string;
  bundle_id: string;
  item_type: BundleItemType;
  item_id: string;
  item_order: number;
  difficulty_mode?: BundleItemDifficulty | null;
}

export interface BundleItemCreate {
  item_type: BundleItemType;
  item_id: string;
  difficulty_mode?: BundleItemDifficulty | null;
}

export interface BundleResponse {
  id: string;
  name: string;
  description?: string | null;
  entity_status: BundleStatus;
  organization_id?: string | null;
  created_by: string;
  created_at: string;
  updated_at?: string;
  items: BundleItem[];
}

export interface BundleListResponse {
  bundles: BundleResponse[];
  pagination: PaginationMeta;
}

export interface BundleCreate {
  name: string;
  description?: string;
  items?: BundleItemCreate[];
  // Only honored for super_admin callers — omit for platform-wide.
  organization_id?: string;
}

export interface BundleUpdate {
  name?: string;
  description?: string;
  items?: BundleItemCreate[];
}

// --- Assignment types ---

export type AssignmentStatus = 'draft' | 'published' | 'expired' | 'archived';
export type AssignmentTargetType = 'user' | 'role' | 'org' | 'class';

export interface AssignmentTarget {
  id: string;
  assignment_id: string;
  target_type: AssignmentTargetType;
  target_id: string;
  /**
   * Labels for a `user` target, resolved server-side on the single-assignment read
   * (`getAssignment`). Absent on list responses, on role/org targets, and for a user
   * whose account no longer resolves. A `class` target's display_name is the class's
   * name, on list responses too.
   */
  display_name?: string | null;
  email?: string | null;
}

export interface AssignmentTargetCreate {
  target_type: AssignmentTargetType;
  target_id: string;
}

export interface ProgressSummary {
  // total/completed/in_progress/not_started count only users who have *engaged* — a
  // targeted user who never opened the assignment has no AssignmentProgress row and so
  // appears in none of them.
  total: number;
  completed: number;
  in_progress: number;
  not_started: number;
  // How many users the assignment actually reaches, resolved from its targets. Needed to
  // tell "all 10 targets finished" from "3 of 10 opened it and those 3 finished", which
  // `completed === total` cannot. null/undefined means the backend could not bound the
  // population (platform-wide assignment with a role/org target) — treat that as "cannot
  // prove completion", never as zero.
  target_total?: number | null;
  // Which kind of null `target_total` is. false/undefined = structural: the population has
  // no bound and won't gain one on a retry. true = the population is bounded but a Keycloak
  // roster lookup failed, so it's unknown only for the moment. Callers must not run a
  // transient unknown through a completion test — "not provably complete" comes out looking
  // like "incomplete", which would turn one blip into a page full of false claims.
  target_lookup_failed?: boolean;
}

export interface AssignmentResponse {
  id: string;
  organization_id?: string | null;
  bundle_ids: string[];
  title: string;
  description?: string;
  assigned_by: string;
  due_date?: string;
  assignment_status: AssignmentStatus;
  created_at: string;
  updated_at?: string;
  targets: AssignmentTarget[];
  progress_summary?: ProgressSummary;
  bundle_names: string[];
  has_started_progress: boolean;
}

export interface AssignmentListResponse {
  assignments: AssignmentResponse[];
  pagination: PaginationMeta;
}

export interface AssignmentCreate {
  bundle_ids: string[];
  title: string;
  description?: string;
  due_date?: string;
  targets: AssignmentTargetCreate[];
  force?: boolean;
  organization_id?: string | null;
}

export interface AssignmentUpdate {
  title?: string;
  description?: string;
  due_date?: string;
  assignment_status?: AssignmentStatus;
  targets?: AssignmentTargetCreate[];
  // Full-replace when present — rejected by the backend once the assignment has real
  // trainee progress (see AssignmentResponse.has_started_progress).
  bundle_ids?: string[];
}

// --- Progress types ---

export type ProgressStatus = 'not_started' | 'in_progress' | 'completed';

export interface AssignmentProgressResponse {
  id: string;
  assignment_id: string;
  user_id: string;
  organization_id: string;
  progress_status: ProgressStatus;
  items_completed: number;
  items_total: number;
  completed_at?: string;
  first_viewed_at?: string;
  created_at: string;
  updated_at?: string;
  display_name?: string | null;
  email?: string | null;
}

export interface AssignmentProgressListResponse {
  progress: AssignmentProgressResponse[];
  pagination: PaginationMeta;
  not_tracked_count: number;
}

export interface AssignmentProgressUpdate {
  progress_status: ProgressStatus;
  items_completed?: number;
  items_total?: number;
}

/** A class assignment's rules (student system). The teacher's preview and the student's
 *  My Assignments render this same shape. */
export interface AssignmentRules {
  max_attempts?: number | null;
  session_minutes?: number | null;
  hints_allowed: boolean;
  objectives: string[];
  rubric?: string | null;
  /** The top of the rubric's scale: scores run from 0 to it. */
  rubric_max_score?: number;
  accommodations?: string | null;
  /** The role the AI plays for the student; null plays the content's own character. */
  practice_type?: PracticeType | null;
}

/** Student practice types (backend `app/classes/student_practice.PracticeType`). */
export const PRACTICE_TYPES = [
  'speech',
  'debate',
  'mock_trial',
  'interview',
  'communication',
  'problem_solving',
  'social_skills',
] as const;
export type PracticeType = (typeof PRACTICE_TYPES)[number];

/** Attempts on one activity of a class assignment. `left` is null when unlimited. */
export interface ItemAttempts {
  bundle_item_id: string;
  used: number;
  left: number | null;
}

export interface MyAssignmentItem {
  assignment: AssignmentResponse;
  progress: AssignmentProgressResponse | null;
  bundle_items: BundleItem[];
  content_deleted?: boolean;
  bundle_names: string[];
  /** Class assignments only. */
  rules?: AssignmentRules | null;
  class_name?: string | null;
  /** Class assignments only: attempts per activity, and the fewest left on any of them
   *  (null when unlimited). */
  attempts?: ItemAttempts[];
  attempts_left?: number | null;
}

// Type-safe helper — guards callers that build a partial MyAssignmentItem before bundle_items are available
export type MyAssignmentItemPartial = Omit<MyAssignmentItem, 'bundle_items'> & { bundle_items?: BundleItem[] };

export interface MyAssignmentResponse {
  assignments: MyAssignmentItem[];
  pagination: PaginationMeta;
}

// --- Helpers ---

function buildQuery(params?: ListParams): string {
  if (!params) return '';
  const q = new URLSearchParams();
  const entries = params as Record<string, string | number | undefined>;
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined) q.set(key, String(value));
  }
  const str = q.toString();
  return str ? `?${str}` : '';
}

async function handleResponse<T>(res: Response, errorPrefix: string): Promise<T> {
  if (!res.ok) {
    let errorMsg = errorPrefix;
    try {
      const e = await res.json();
      errorMsg = e.detail || errorMsg;
    } catch {
      // body was not JSON — keep the prefix message
    }
    logger.error(errorMsg, { status: res.status, url: res.url });
    throw new Error(errorMsg);
  }
  return res.json() as Promise<T>;
}

async function handleVoidResponse(res: Response, errorPrefix: string): Promise<void> {
  if (!res.ok) {
    let errorMsg = errorPrefix;
    try {
      const e = await res.json();
      errorMsg = e.detail || errorMsg;
    } catch {
      // body was not JSON — keep the prefix message
    }
    logger.error(errorMsg, { status: res.status, url: res.url });
    throw new Error(errorMsg);
  }
}

function authHeaders(token: string): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// --- Bundle CRUD ---

export async function createBundle(token: string, data: BundleCreate): Promise<BundleResponse> {
  const res = await fetch(`${getBackendUrl()}/bundles/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<BundleResponse>(res, 'Failed to create bundle');
}

export async function listBundles(token: string, params?: ListParams): Promise<BundleListResponse> {
  const res = await fetch(`${getBackendUrl()}/bundles/${buildQuery(params)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<BundleListResponse>(res, 'Failed to list bundles');
}

export async function getBundle(token: string, bundleId: string): Promise<BundleResponse> {
  const res = await fetch(`${getBackendUrl()}/bundles/${bundleId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<BundleResponse>(res, 'Failed to get bundle');
}

export async function updateBundle(token: string, bundleId: string, data: BundleUpdate): Promise<BundleResponse> {
  const res = await fetch(`${getBackendUrl()}/bundles/${bundleId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<BundleResponse>(res, 'Failed to update bundle');
}

export async function deleteBundle(token: string, bundleId: string): Promise<void> {
  const res = await fetch(`${getBackendUrl()}/bundles/${bundleId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleVoidResponse(res, 'Failed to delete bundle');
}

export async function publishBundle(token: string, bundleId: string): Promise<BundleResponse> {
  const res = await fetch(`${getBackendUrl()}/bundles/${bundleId}/publish`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });
  return handleResponse<BundleResponse>(res, 'Failed to publish bundle');
}

// --- Assignment CRUD ---

export async function createAssignment(token: string, data: AssignmentCreate): Promise<AssignmentResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<AssignmentResponse>(res, 'Failed to create assignment');
}

export async function listAssignments(token: string, params?: ListParams): Promise<AssignmentListResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/${buildQuery(params)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AssignmentListResponse>(res, 'Failed to list assignments');
}

export async function getAssignment(token: string, assignmentId: string): Promise<AssignmentResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/${assignmentId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AssignmentResponse>(res, 'Failed to get assignment');
}

export async function updateAssignment(
  token: string,
  assignmentId: string,
  data: AssignmentUpdate,
): Promise<AssignmentResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/${assignmentId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<AssignmentResponse>(res, 'Failed to update assignment');
}

export async function deleteAssignment(token: string, assignmentId: string): Promise<void> {
  const res = await fetch(`${getBackendUrl()}/assignments/${assignmentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleVoidResponse(res, 'Failed to delete assignment');
}

// --- Progress ---

export async function getMyAssignments(token: string, params?: Pick<ListParams, 'page' | 'limit' | 'search'>): Promise<MyAssignmentResponse> {
  const qs = buildQuery(params);
  const res = await fetch(`${getBackendUrl()}/assignments/me${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<MyAssignmentResponse>(res, 'Failed to get my assignments');
}

export async function getMyAssignmentsUnviewedCount(token: string): Promise<number> {
  const res = await fetch(`${getBackendUrl()}/assignments/me/unviewed-count`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await handleResponse<{ unviewed_count: number }>(res, 'Failed to get unviewed assignment count');
  return data.unviewed_count;
}

export async function markAssignmentsViewed(token: string): Promise<void> {
  const res = await fetch(`${getBackendUrl()}/assignments/me/mark-viewed`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return handleVoidResponse(res, 'Failed to mark assignments as viewed');
}

export async function getAssignmentProgress(
  token: string,
  assignmentId: string,
  params?: ListParams,
): Promise<AssignmentProgressListResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/${assignmentId}/progress${buildQuery(params)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return handleResponse<AssignmentProgressListResponse>(res, 'Failed to get assignment progress');
}

export async function updateUserProgress(
  token: string,
  assignmentId: string,
  userId: string,
  data: AssignmentProgressUpdate,
): Promise<AssignmentProgressResponse> {
  const res = await fetch(`${getBackendUrl()}/assignments/${assignmentId}/progress/${userId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  return handleResponse<AssignmentProgressResponse>(res, 'Failed to update user progress');
}
